import { isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { AppActionTypes } from "@app/store/actions/app.actions";
import { HideFullscreenImage } from "@app/store/actions/fullscreen-image.actions";
import { LoadThumbnail } from "@app/store/actions/thumbnail.actions";
import {
  selectCurrentFullscreenImage,
  selectCurrentFullscreenImageEvent
} from "@app/store/selectors/app/app.selectors";
import { selectImage } from "@app/store/selectors/app/image.selectors";
import { selectThumbnail } from "@app/store/selectors/app/thumbnail.selectors";
import { MainState } from "@app/store/state";
import { ImageAlias } from "@core/enums/image-alias.enum";
import { ImageThumbnailInterface } from "@core/interfaces/image-thumbnail.interface";
import {
  FINAL_REVISION_LABEL,
  FullSizeLimitationDisplayOptions,
  ImageInterface,
  ImageRevisionInterface,
  ORIGINAL_REVISION_LABEL
} from "@core/interfaces/image.interface";
import { SolutionStatus } from "@core/interfaces/solution.interface";
import { ClassicRoutesService } from "@core/services/classic-routes.service";
import { CoordinatesFormatterService } from "@core/services/coordinates-formatter.service";
import { DeviceService } from "@core/services/device.service";
import { ImageService } from "@core/services/image/image.service";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { SolvedCoordinatesService } from "@core/services/solved-coordinates.service";
import { SwipeDownService } from "@core/services/swipe-down.service";
import { TitleService } from "@core/services/title/title.service";
import { TouchZoomService } from "@core/services/touch-zoom.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Actions, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { fadeInOut } from "@shared/animations";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { AnnotationService } from "@shared/components/misc/annotation-tool/services/annotation.service";
import { CookieService } from "ngx-cookie";
import { Coord, NgxImageZoomComponent } from "ngx-image-zoom";
import { ActiveToast } from "ngx-toastr";
import { BehaviorSubject, combineLatest, interval, Observable, of, Subscription } from "rxjs";
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap
} from "rxjs/operators";

declare type HammerInput = any;

@Component({
  selector: "astrobin-fullscreen-image-viewer",
  templateUrl: "./fullscreen-image-viewer.component.html",
  styleUrls: ["./fullscreen-image-viewer.component.scss"],
  animations: [fadeInOut],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FullscreenImageViewerComponent extends BaseComponentDirective implements OnInit, OnChanges, OnDestroy {
  @Input()
  id: ImageInterface["pk"];

  @Input()
  revisionLabel = FINAL_REVISION_LABEL;

  @Input()
  anonymized = false;

  @Input()
  respectFullSizeDisplayLimitation = true;

  // We use `standalone = true` when opening a fullscreen image viewer from a standalone image viewer, i.e. an image
  // viewer that comes from an image page and not a slideshow that's opened dynamically.
  @Input()
  standalone = false;

  @Input()
  eagerLoading = false;

  @Input()
  externalSolutionMatrix: {
    matrixRect: string;
    matrixDelta: number;
    raMatrix: string;
    decMatrix: string;
  };

  @Output()
  enterFullscreen = new EventEmitter<void>();

  @Output()
  exitFullscreen = new EventEmitter<void>();

  @ViewChild("ngxImageZoom", { static: false, read: NgxImageZoomComponent })
  ngxImageZoom: NgxImageZoomComponent;

  @ViewChild("ngxImageZoom", { static: false, read: ElementRef })
  ngxImageZoomEl: ElementRef;

  @ViewChild("touchRealContainer", { static: false })
  touchRealContainer: ElementRef;

  @ViewChild("touchRealCanvas")
  touchRealCanvas: ElementRef<HTMLCanvasElement>;

  @ViewChild("touchRealWrapper", { static: false })
  touchRealWrapper: ElementRef;

  @ViewChild("staticImage", { static: false })
  staticImageEl: ElementRef;

  @ViewChild("staticImageContainer", { static: false })
  staticImageContainer: ElementRef;

  @HostBinding("class.show")
  show = false;

  protected readonly Math = Math;

  // Flag to easily check if we're running in a browser environment
  protected readonly isBrowser: boolean;

  // We initialize this to a default value, but it will be updated with minZoomRatio in _initImageZoom
  protected zoomScroll = 0;
  protected touchMode?: boolean = undefined;
  protected enableLens = true;
  protected zoomLensSize: number;
  protected showZoomIndicator = false;
  protected isHybridPC = false;
  protected isTouchDevice = false;
  protected hdThumbnail: SafeUrl;
  protected realThumbnail: SafeUrl;
  protected realThumbnailUnsafeUrl: string;
  protected hdImageLoadingProgress$: Observable<number>;
  protected realImageLoadingProgress$: Observable<number>;
  protected loadingProgress$: Observable<number>;
  protected hdThumbnailLoading = false;
  protected realThumbnailLoading = false;
  protected ready = false;
  protected allowReal = false;
  protected allowZoom = true;
  protected touchScale = 1;
  protected naturalWidth: number;
  protected naturalHeight: number;
  protected maxZoom = 8;
  protected canvasLoading = false;
  protected isGif = false;
  protected zoomFrozen = false;
  protected mouseCoords: {
    raHtml: string;
    decHtml: string;
    galacticRaHtml: string;
    galacticDecHtml: string;
  } = null;
  protected showCoordinates: boolean = false;
  protected showKebabMenu: boolean = false;
  protected showKeyboardShortcutsOverlay: boolean = false;

  // Measuring tool properties
  protected advancedSolutionMatrix: {
    matrixRect: string;
    matrixDelta: number;
    raMatrix: string;
    decMatrix: string;
  };
  protected loadingAdvancedSolutionMatrix = false;
  protected isMeasuringMode: boolean = false;
  protected isImageOwner: boolean = false;
  protected measurementInProgress: boolean = false;
  protected measureStartPoint: { x: number; y: number; ra: number; dec: number } = null;
  protected measureEndPoint: { x: number; y: number; ra: number; dec: number } = null;
  protected measureDistance: string = null;

  // Annotation tool properties
  protected isAnnotationMode: boolean = false;
  protected annotationReadOnlyMode: boolean = true; // Default to read-only mode

  // Swipe-down properties
  protected touchStartY: { value: number } = { value: 0 };
  protected touchCurrentY: { value: number } = { value: 0 };
  protected touchPreviousY: { value: number } = { value: 0 };
  protected isSwiping: { value: boolean } = { value: false };
  protected swipeProgress: { value: number } = { value: 0 };
  protected swipeThreshold = 150;
  protected swipeDirectionDown: { value: boolean } = { value: true };

  // Mouse position for crosshair rulers
  protected mouseX: number = null;
  protected mouseY: number = null;
  protected crosshairLeft: number = 0;
  protected crosshairTop: number = 0;
  protected crosshairWidth: number = 0;
  protected crosshairHeight: number = 0;
  protected isMouseOverUIElement: boolean = false;
  protected image: ImageInterface;
  protected revision: ImageInterface | ImageRevisionInterface;
  // Flag to track if static image has loaded
  protected isStaticImageLoaded = false;
  // Property to hold the image element for coordinate tracking
  protected imageElementForCoordinateTracker: HTMLImageElement | null = null;
  // Track the "Activate zoom first" notification
  private _zoomActivationNotification: ActiveToast<any> | null = null;
  // Track the "Measuring tool only available at default zoom" notification
  private _measureZoomNotification: ActiveToast<any> | null = null;
  private _animationFrame: number = null;
  private _imageSubscription: Subscription;
  private _hdThumbnailSubscription: Subscription;
  private _realThumbnailSubscription: Subscription;
  private _currentFullscreenImageSubscription: Subscription;
  private _currentFullscreenImageEventSubscription: Subscription;
  // No need for timeout reference with utilsService.delay
  private _zoomIndicatorTimeoutDuration = 1000;
  private _hdLoadingProgressSubject = new BehaviorSubject<number>(0);
  private _realLoadingProgressSubject = new BehaviorSubject<number>(0);
  private _eagerLoadingSubscription: Subscription;

  // BehaviorSubjects for reactive measuring mode activation
  private isMeasuringRequested$ = new BehaviorSubject<boolean>(false);
  private isStaticImageLoaded$ = new BehaviorSubject<boolean>(false);
  private isMatrixLoaded$ = new BehaviorSubject<boolean>(false);
  private isFullscreenActive$ = new BehaviorSubject<boolean>(false);
  private pendingMeasurementToolActivation = false;
  private readonly LENS_ENABLED_COOKIE_NAME = "astrobin-fullscreen-lens-enabled";
  private readonly TOUCH_OR_MOUSE_MODE_COOKIE_NAME = "astrobin-fullscreen-touch-or-mouse";
  private readonly COORDINATES_ENABLED_COOKIE_NAME = "astrobin-fullscreen-show-coordinates";
  private readonly PIXEL_THRESHOLD = 8192 * 8192;
  private readonly FRAME_INTERVAL = 1000 / 120; // 120 FPS
  // Store original handlers and position for freezing/unfreezing
  private _originalOnMouseMove: any = null;
  private _originalOnMouseWheel: any = null;
  private _initTouchZoomRetryCount = 0;

  /**
   * Clears the coordinate display when mouse leaves the fullscreen viewer
   */
  // This method is implemented below as protected clearCoordinates()

  constructor(
    public readonly store$: Store<MainState>,
    public readonly actions$: Actions,
    public readonly windowRef: WindowRefService,
    public readonly translateService: TranslateService,
    public readonly imageService: ImageService,
    public readonly domSanitizer: DomSanitizer,
    public readonly utilsService: UtilsService,
    @Inject(PLATFORM_ID) public readonly platformId: Object,
    public readonly deviceService: DeviceService,
    public readonly cookieService: CookieService,
    public readonly classicRoutesService: ClassicRoutesService,
    public readonly windowRefService: WindowRefService,
    public readonly titleService: TitleService,
    public readonly renderer: Renderer2,
    public readonly changeDetectorRef: ChangeDetectorRef,
    public readonly swipeDownService: SwipeDownService,
    public readonly popNotificationsService: PopNotificationsService,
    public readonly hostElementRef: ElementRef,
    public readonly coordinatesFormatter: CoordinatesFormatterService,
    public readonly solvedCoordinatesService: SolvedCoordinatesService,
    public readonly activatedRoute: ActivatedRoute,
    public readonly modalService: NgbModal,
    public readonly touchZoom: TouchZoomService,
    public readonly annotationService: AnnotationService
  ) {
    super(store$);

    // Initialize the browser flag
    this.isBrowser = isPlatformBrowser(platformId);

    this.isHybridPC = this.deviceService.isHybridPC();
    this.isTouchDevice = this.deviceService.isTouchEnabled();

    const touchModeCookie = this.cookieService.get(this.TOUCH_OR_MOUSE_MODE_COOKIE_NAME);
    this.touchMode = this.cookieService.get(this.TOUCH_OR_MOUSE_MODE_COOKIE_NAME) === "touch";

    if (!touchModeCookie) {
      this.setTouchMouseMode(this.isTouchDevice);
    } else {
      this.touchMode = touchModeCookie === "touch";
    }

    this.enableLens = this.cookieService.get(this.LENS_ENABLED_COOKIE_NAME) === "true";
    this.showCoordinates = this.cookieService.get(this.COORDINATES_ENABLED_COOKIE_NAME) === "true";
    this.hdImageLoadingProgress$ = this._hdLoadingProgressSubject.asObservable();
    this.realImageLoadingProgress$ = this._realLoadingProgressSubject.asObservable();
    this.loadingProgress$ = combineLatest([
      this.hdImageLoadingProgress$.pipe(startWith(0)),
      this.realImageLoadingProgress$.pipe(startWith(0))
    ]).pipe(
      // First 50% is HD, second 50% is REAL
      map(([hdProgress, realProgress]) => {
        if (this.hdThumbnailLoading) {
          return hdProgress * 0.5;
        }
        if (this.realThumbnailLoading) {
          return 50 + realProgress * 0.5;
        }
        if (this.canvasLoading) {
          return 100; // Keep full progress during canvas init
        }
        return null;
      }),
      distinctUntilChanged()
    );
  }

  @HostBinding("class.disable-zoom")
  get disableZoomClass() {
    return this.realThumbnailLoading || this.hdThumbnailLoading;
  }

  @HostBinding("class.standalone")
  get standaloneClass() {
    return this.standalone;
  }

  @HostBinding("class.zoom-disabled")
  get zoomDisabledClass() {
    return !this.allowZoom;
  }

  get zoomingEnabled(): boolean {
    return this.ngxImageZoom?.zoomService.zoomingEnabled;
  }

  // Returns the maximum width for the image - either naturalWidth or window width, whichever is smaller
  get maxDisplayWidth(): number {
    if (!this.isBrowser || !this.windowRef?.nativeWindow) {
      return this.naturalWidth || 1000; // Fallback for SSR or no window ref
    }

    const availableWidth = this.windowRef.nativeWindow.innerWidth;

    if (!this.naturalWidth) {
      return availableWidth;
    }

    return Math.min(this.naturalWidth, availableWidth);
  }

  protected get isVeryLargeImage(): boolean {
    return this.naturalWidth * (this.naturalHeight || this.naturalWidth) > this.PIXEL_THRESHOLD;
  }

  protected get hasAdvancedSolution(): boolean {
    return !!(
      this.revision?.solution?.status === SolutionStatus.ADVANCED_SUCCESS &&
      this.revision?.solution?.advancedRa &&
      this.revision?.solution?.advancedDec
    );
  }

  protected get hasAdvancedSolutionMatrix(): boolean {
    return !!this.advancedSolutionMatrix && !!this.advancedSolutionMatrix.matrixRect;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isBrowser) {
      return;
    }

    // If the kebab menu is open and the click is outside the menu, close it
    if (this.showKebabMenu) {
      const kebabContainer = (event.target as HTMLElement).closest(".kebab-menu-container");
      if (!kebabContainer) {
        this.showKebabMenu = false;
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  @HostListener("window:resize", ["$event"])
  onResize(event) {
    if (!this.isBrowser) {
      return;
    }

    // First reset zoom completely - we'll reinitialize from scratch
    this.resetToDefaultZoom();

    // Update dimensions
    this._setZoomLensSize();

    // If in touch mode, use TouchZoomService to update dimensions
    if (this.touchMode && !this.isVeryLargeImage && this.touchRealCanvas && this.touchRealContainer) {
      // Add delay to ensure DOM has updated, especially important in PWA mode during rotation
      this.utilsService.delay(100).subscribe(() => {
        // Update the TouchZoomService with the new dimensions
        this.touchZoom.updateDimensions(this.touchRealCanvas.nativeElement, this.touchRealContainer.nativeElement);
      });
    }

    // For non-touch mode, completely reinitialize the zoom component
    if (!this.touchMode && this.ngxImageZoom && !this.isVeryLargeImage) {
      // Force zoom component reinitialization using delay
      // This ensures DOM has updated before we try to access elements
      this.utilsService.delay(100).subscribe(() => {
        // If we have access to _initImageZoom, call it directly
        if (typeof this._initImageZoom === "function") {
          this._initImageZoom(false); // Pass false to avoid keeping current zoom state
        } else {
          // Otherwise manually initialize key zoom properties
          const zoomContainer = this.ngxImageZoomEl?.nativeElement;
          if (zoomContainer) {
            const thumbnailElem = zoomContainer.querySelector(".ngxImageZoomThumbnail");
            if (thumbnailElem) {
              // Recalculate thumbnail dimensions based on current rendered size
              const renderedThumbnailHeight = thumbnailElem.height;
              const thumbnailNaturalHeight = thumbnailElem.naturalHeight;
              const renderRatio = renderedThumbnailHeight / thumbnailNaturalHeight;
              const renderedThumbnailWidth = thumbnailElem.naturalWidth * renderRatio;

              // Update core zoom service properties with new dimensions
              if (this.ngxImageZoom.zoomService) {
                this.ngxImageZoom.zoomService.thumbWidth = renderedThumbnailWidth;
                this.ngxImageZoom.zoomService.thumbHeight = renderedThumbnailHeight;
                this.ngxImageZoom.zoomService.minZoomRatio = renderedThumbnailWidth / this.naturalWidth;
                this.ngxImageZoom.zoomService.calculateRatio();

                // Make sure zoom is off initially
                this.ngxImageZoom.zoomService.zoomOff();
              }
            }
          }
        }

        // Force change detection to update UI
        this.changeDetectorRef.markForCheck();
      });
    }

    // Clear coordinate display until next mouse move
    this.mouseCoords = null;

    // Reset crosshair position
    this.mouseX = null;
    this.mouseY = null;
    this.crosshairLeft = 0;
    this.crosshairTop = 0;
    this.crosshairWidth = 0;
    this.crosshairHeight = 0;

    this.changeDetectorRef.markForCheck();

    // Re-trigger coordinate calculation on next mouse movement
    if (this.revision && this.windowRef.nativeWindow) {
      // Use requestAnimationFrame to wait for the resize to finish
      this.windowRef.nativeWindow.requestAnimationFrame(() => {
        // If we have the current mouse position, simulate a mouse move event to update coordinates
        const lastMouseEvent = this.windowRef.nativeWindow.event as MouseEvent;
        if (lastMouseEvent && lastMouseEvent.type === "mousemove") {
          this.onGlobalMouseMove(lastMouseEvent);
        }
      });
    }
  }

  ngOnInit() {
    this._setZoomLensSize();

    // Set up reactive measuring mode activation
    combineLatest([
      this.isFullscreenActive$.pipe(distinctUntilChanged()),
      this.isMeasuringRequested$.pipe(distinctUntilChanged()),
      this.isStaticImageLoaded$.pipe(distinctUntilChanged()),
      this.isMatrixLoaded$.pipe(distinctUntilChanged())
    ])
      .pipe(
        // Filter for the state where all conditions are true
        filter(
          ([isActive, requested, staticReady, matrixReady]) => isActive && requested && staticReady && matrixReady
        ),
        // Ensure cleanup on component destruction
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        // Activate measuring mode if not already active
        this._activateMeasuringMode();
      });

    // Check if URL contains shared measurements or annotations
    if (this.isBrowser) {
      // Check URL directly for measurements and annotations parameters
      const currentUrl = new URL(this.windowRef.nativeWindow.location.href);
      const measurementsParam = currentUrl.searchParams.get("measurements");
      const annotationsParam = currentUrl.searchParams.get("annotations");
      const revisionParam = currentUrl.searchParams.get("r"); // Use 'r' parameter for revision

      // Check for revision parameter in URL - but only use it if provided explicitly
      // This ensures that the @Input property has the highest priority when no specific revision is requested
      if (revisionParam && revisionParam !== FINAL_REVISION_LABEL) {
        // Only override the input value if URL explicitly specifies a non-default revision
        this.revisionLabel = revisionParam;
      }

      // First, check with the router's query params
      this.activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
        // Check for revision in query params as well - only override if explicit non-default
        if (params.r && params.r !== FINAL_REVISION_LABEL) {
          this.revisionLabel = params.r;
        }

        // Prioritize measurements over annotations
        if (params.measurements || measurementsParam) {
          // Remove annotations parameter from URL if it exists
          if (annotationsParam) {
            currentUrl.searchParams.delete("annotations");
            // Update the URL without navigation
            this.windowRef.replaceState({}, currentUrl.toString());
          }

          // Instead of directly activating, signal a request
          this.isMeasuringRequested$.next(true);
          // If both parameters are present, measurement tool takes priority
          this.isAnnotationMode = false;
        }
        // Only handle annotations if measurement tool is not activated
        else if (params.annotations || annotationsParam) {
          // Directly activate annotation mode - no need to wait for image zoom
          this.isAnnotationMode = true;
          // Enable editing - annotations are always editable in fullscreen
          this.annotationReadOnlyMode = false;
        }

        // Force change detection to update templates
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.id === undefined) {
      throw new Error("Attribute 'id' is required");
    }

    if (changes.id || changes.revisionLabel) {
      this.isStaticImageLoaded = false;
      this.isStaticImageLoaded$.next(false);
    }

    if (changes.eagerLoading || changes.id) {
      // Clean up previous subscriptions
      if (this._eagerLoadingSubscription) {
        this._eagerLoadingSubscription.unsubscribe();
        this._eagerLoadingSubscription = null;
      }

      this.hdThumbnail = null;
      this.realThumbnail = null;

      // Set up eager loading if enabled
      if (this.eagerLoading && !this._eagerLoadingSubscription) {
        this.utilsService.delay(2500).subscribe(() => {
          // Double check in case user clicked during timeout
          if (!this._eagerLoadingSubscription && !this.hdThumbnail && !this.realThumbnail) {
            this._eagerLoadingSubscription = this._initThumbnailSubscriptions();
            this.changeDetectorRef.markForCheck();
          }
        });
      }
    }

    // Reset the solution matrix when the revision changes
    if (changes.revisionLabel) {
      // Store the new revision label explicitly
      const newRevisionLabel = changes.revisionLabel.currentValue;

      // Clear the current solution matrix to force a reload for the new revision
      this.advancedSolutionMatrix = null;
      this.loadingAdvancedSolutionMatrix = false;
      this.isMatrixLoaded$.next(false);

      // If we have the image loaded already, reload the revision and matrix
      if (this.image) {
        // Force a reload of the revision with the new label
        this.revision = this.imageService.getRevision(this.image, newRevisionLabel);

        // If the new revision has a solution, ensure the matrix is loaded
        if (this.revision?.solution?.id) {
          this.loadingAdvancedSolutionMatrix = true;
          this.solvedCoordinatesService.ensureMatrixLoaded(this.revision.solution.id).subscribe(matrix => {
            this.advancedSolutionMatrix = matrix;
            this.loadingAdvancedSolutionMatrix = false;
            this.isMatrixLoaded$.next(!!matrix);

            // Check if we have a pending activation request
            if (this.pendingMeasurementToolActivation && !this.isMeasuringMode && !!matrix) {
              // Try to activate immediately if we can
              if (this.isStaticImageLoaded$.value && this.isFullscreenActive$.value) {
                this._activateMeasuringMode();
                this.pendingMeasurementToolActivation = false;
              }
            }

            this.changeDetectorRef.markForCheck();
          });
        }
      }

      // Force change detection
      this.changeDetectorRef.markForCheck();
    }

    if (this._currentFullscreenImageSubscription) {
      this._currentFullscreenImageSubscription.unsubscribe();
    }

    if (this._currentFullscreenImageEventSubscription) {
      this._currentFullscreenImageEventSubscription.unsubscribe();
    }

    this._currentFullscreenImageSubscription = this.store$
      .pipe(
        select(selectCurrentFullscreenImage),
        distinctUntilChanged(),
        filter(currentFullscreenImage => currentFullscreenImage === this.id)
      )
      .subscribe(() => {
        this.actions$.pipe(ofType(AppActionTypes.HIDE_FULLSCREEN_IMAGE), take(1)).subscribe(() => {
          this._resetThumbnailSubscriptions();
          this.show = false;
          // Reset all state flags
          this.isFullscreenActive$.next(false);
          this.isMeasuringRequested$.next(false);
          this.isStaticImageLoaded$.next(false);
          this.isMatrixLoaded$.next(false);
          this.isMeasuringMode = false; // Explicitly turn off mode when hiding

          this.hdThumbnail = null;
          this.realThumbnail = null;
          this._resetTouchZoom();
          this.titleService.enableNativeGestures();

          this._resetCanvas();

          // Clean up all styles and classes
          this._cleanupHostStyles();

          cancelAnimationFrame(this._animationFrame);
          this.exitFullscreen.emit();
          this.changeDetectorRef.markForCheck();
        });

        this._currentFullscreenImageEventSubscription = this.store$
          .pipe(select(selectCurrentFullscreenImageEvent), take(1))
          .subscribe(event => {
            if (this._isTouchEvent(event)) {
              this.setTouchMouseMode(true);
              this.changeDetectorRef.markForCheck();
            } else if (event instanceof MouseEvent) {
              this.setTouchMouseMode(false);
              this.changeDetectorRef.markForCheck();
            }

            // Remove any inline styles and animation classes that might be preventing the component from showing
            this.renderer.removeStyle(this.hostElementRef.nativeElement, "transform");
            this.renderer.removeStyle(this.hostElementRef.nativeElement, "opacity");
            this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-animating");
            this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-animate");
            this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-return-to-normal");

            this.show = true;
            this.isFullscreenActive$.next(true);
            this.titleService.disableNativeGestures();
            // Add the fullscreen class to the body
            if (this.isBrowser && typeof document !== "undefined") {
              document.body.classList.add("fullscreen-image-viewer-active");
            }
            this.enterFullscreen.emit();

            // Only initialize thumbnails if not already eagerly loading
            if (!this._eagerLoadingSubscription && !this.hdThumbnail && !this.realThumbnail) {
              this._initThumbnailSubscriptions();
            }

            // Check if we should enable annotation mode from SHOW_FULLSCREEN_IMAGE action
            this.actions$
              .pipe(
                ofType(AppActionTypes.SHOW_FULLSCREEN_IMAGE),
                filter((action: any) => action.payload && action.payload.imageId === this.id),
                take(1)
              )
              .subscribe((action: any) => {
                // Check if zoom is allowed (based on owner's settings)
                if (action.payload.hasOwnProperty("allowZoom")) {
                  this.allowZoom = action.payload.allowZoom;
                }

                // Check if a specific revision was specified in the action payload
                if (action.payload.hasOwnProperty("revisionLabel") && action.payload.revisionLabel) {
                  this.revisionLabel = action.payload.revisionLabel;
                }

                // Check if measurements should be activated via the action payload (from button click) - this gets highest priority
                if (action.payload.activateMeasurementTool) {
                  // Ensure annotations are disabled
                  this.isAnnotationMode = false;

                  // First set the flags
                  this.isMeasuringRequested$.next(true);
                  this.pendingMeasurementToolActivation = true;

                  // Update URL with measurements parameter
                  if (this.isBrowser) {
                    try {
                      const location = this.windowRef.nativeWindow.location;
                      const currentUrl = new URL(location.href);

                      if (!currentUrl.searchParams.has("measurements")) {
                        currentUrl.searchParams.set("measurements", "1");
                        this.windowRef.replaceState({}, currentUrl.toString());
                      }
                    } catch (e) {
                      console.error("Error adding measurements parameter to URL:", e);
                    }
                  }

                  // Set up proper retry mechanism with RxJS
                  // First give the app a moment to initialize components
                  this.utilsService
                    .delay(300)
                    .pipe(
                      // Then set up polling until measurement tool activates or component is destroyed
                      switchMap(() =>
                        interval(500).pipe(
                          // Continue until measuring mode is active or component is destroyed or request is canceled
                          takeWhile(() => !this.isMeasuringMode && this.pendingMeasurementToolActivation),
                          // Ensure we stop on component destruction
                          takeUntil(this.destroyed$),
                          // Check conditions on each interval
                          tap(() => {
                            // Activate if conditions are met
                            if (
                              this.isStaticImageLoaded &&
                              this.advancedSolutionMatrix &&
                              this.show &&
                              !this.isMeasuringMode &&
                              this.pendingMeasurementToolActivation
                            ) {
                              this.isMeasuringMode = true;
                              this.pendingMeasurementToolActivation = false;
                              this.changeDetectorRef.markForCheck();
                            }
                          })
                        )
                      ),
                      // Handle any errors to prevent uncaught exceptions
                      catchError(err => {
                        console.error("Error in measuring mode activation retry:", err);
                        return of(null);
                      })
                    )
                    .subscribe();

                  // Try immediate activation first in case everything is already loaded
                  if (this.isStaticImageLoaded && this.advancedSolutionMatrix) {
                    this.isMeasuringMode = true;
                    this.pendingMeasurementToolActivation = false;
                    this.changeDetectorRef.markForCheck();
                  }

                  return; // Exit early since we've handled the activation
                }

                // Check for parameters in URL (lower priority than action payload)
                const currentUrl = new URL(this.windowRef.nativeWindow.location.href);
                const measurementsParam = currentUrl.searchParams.get("measurements");
                const annotationsParam = currentUrl.searchParams.get("annotations");
                const revisionParam = currentUrl.searchParams.get("revision");

                // Check for revision parameter in URL
                if (revisionParam) {
                  this.revisionLabel = revisionParam;
                }

                if (measurementsParam) {
                  // Remove annotations parameter from URL if it exists
                  if (annotationsParam) {
                    currentUrl.searchParams.delete("annotations");
                    // Update the URL without navigation
                    this.windowRef.replaceState({}, currentUrl.toString());
                  }

                  // If measurements parameter is present, ensure annotations are disabled
                  this.isAnnotationMode = false;
                  // Signal measuring mode is requested (will be activated by the combineLatest logic)
                  this.isMeasuringRequested$.next(true);
                  // If both static image and matrix are already loaded, activate immediately
                  if (this.isStaticImageLoaded$.value && this.isMatrixLoaded$.value) {
                    this._activateMeasuringMode();
                  } else {
                    this.pendingMeasurementToolActivation = true;
                  }
                  this.changeDetectorRef.markForCheck();
                }
                // Only enable annotations if measurements are not active
                else if (action.payload.enableAnnotations && !this.isMeasuringMode) {
                  // Enable annotation mode after a short delay to ensure component is fully initialized
                  this.utilsService.delay(300).subscribe(() => {
                    // Enable annotation mode without relying on URL parameters
                    this.isAnnotationMode = true;
                    // Enable editing - annotations are always editable in fullscreen
                    this.annotationReadOnlyMode = false;
                    this.changeDetectorRef.markForCheck();
                  });
                }
              });

            this.changeDetectorRef.markForCheck();
          });
      });
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    // Clear any measuring zoom notification
    if (this._measureZoomNotification) {
      this.popNotificationsService.clear(this._measureZoomNotification.toastId);
      this._measureZoomNotification = null;
    }

    // Restore original event handlers if needed
    this._restoreOriginalEventHandlers();

    // Clean up TouchZoomService resources
    this.touchZoom.destroy();

    if (this._eagerLoadingSubscription) {
      this._eagerLoadingSubscription.unsubscribe();
      this._eagerLoadingSubscription = null;
    }

    if (this._imageSubscription) {
      this._imageSubscription.unsubscribe();
      this._imageSubscription = null;
    }

    if (this._hdThumbnailSubscription) {
      this._hdThumbnailSubscription.unsubscribe();
      this._hdThumbnailSubscription = null;
    }

    if (this._realThumbnailSubscription) {
      this._realThumbnailSubscription.unsubscribe();
      this._realThumbnailSubscription = null;
    }

    if (this._currentFullscreenImageSubscription) {
      this._currentFullscreenImageSubscription.unsubscribe();
      this._currentFullscreenImageSubscription = null;
    }
  }

  onImagesLoaded(loaded: boolean) {
    this.ready = loaded;
    this._initImageZoom();

    // Check for measurements parameter in URL after images are loaded
    if (this.isBrowser && loaded) {
      const currentUrl = new URL(this.windowRef.nativeWindow.location.href);
      const measurementsParam = currentUrl.searchParams.get("measurements");
      const annotationsParam = currentUrl.searchParams.get("annotations");
      if (measurementsParam && !this.isMeasuringMode) {
        // Remove annotations parameter from URL if it exists
        if (annotationsParam) {
          currentUrl.searchParams.delete("annotations");
          // Update the URL without navigation
          this.windowRef.replaceState({}, currentUrl.toString());
        }

        // Signal measuring mode is requested (will be activated by the combineLatest logic)
        this.isMeasuringRequested$.next(true);

        // If annotation mode is active, disable it immediately
        if (this.isAnnotationMode) {
          this.isAnnotationMode = false;
        }

        // Try to activate immediately if we can
        if (this.isStaticImageLoaded$.value && this.isMatrixLoaded$.value && this.isFullscreenActive$.value) {
          this._activateMeasuringMode();
        }
      }
      // Only handle annotations if measurement tool is not activated
      else if (annotationsParam && !this.isMeasuringMode && !this.isAnnotationMode) {
        // Activate annotation mode after a slight delay to ensure all components are ready
        this.utilsService.delay(100).subscribe(() => {
          // Enable annotation mode
          this.isAnnotationMode = true;
          // Enable editing - annotations are always editable in fullscreen
          this.annotationReadOnlyMode = false;
          this.changeDetectorRef.markForCheck();
        });
      }
    }

    this.changeDetectorRef.markForCheck();
  }

  /**
   * Programmatically activates the measuring mode if conditions are met
   */
  activateMeasuringMode(): boolean {
    // Only proceed if the component is shown in fullscreen
    if (!this.show) {
      return false;
    }

    // State checking for activation conditions already handled by the reactive stream

    // Request measuring mode activation
    this.isMeasuringRequested$.next(true);

    // If static image and matrix are already loaded, activate immediately
    if (this.isStaticImageLoaded$.value && this.isMatrixLoaded$.value) {
      this._activateMeasuringMode();
      return true;
    }

    return false;
  }

  /**
   * Private method to handle the actual activation of measuring mode
   */
  private _activateMeasuringMode(): void {
    // Activate measuring mode if not already active
    if (!this.isMeasuringMode) {
      this.isMeasuringMode = true;

      // Reset pending flag
      this.pendingMeasurementToolActivation = false;

      // Ensure annotation mode is disabled if active
      if (this.isAnnotationMode) {
        this.isAnnotationMode = false;
      }

      // Update URL with measurements parameter if not already present
      if (this.isBrowser) {
        try {
          const location = this.windowRef.nativeWindow.location;
          const currentUrl = new URL(location.href);

          if (!currentUrl.searchParams.has("measurements")) {
            currentUrl.searchParams.set("measurements", "1");
            this.windowRef.replaceState({}, currentUrl.toString());
          }
        } catch (e) {
          console.error("Error adding measurements parameter to URL:", e);
        }
      }

      // Trigger change detection
      this.changeDetectorRef.markForCheck();
    } else {
    }
  }

  /**
   * Called when the static image has loaded
   * This is specifically for measuring/annotation modes using the static template
   */
  onStaticImageLoaded() {
    if (!this.isBrowser) {
      return;
    }

    // Static image loaded successfully
    this.isStaticImageLoaded = true;
    // Update the BehaviorSubject
    this.isStaticImageLoaded$.next(true);

    // Check for measurements parameter in URL after static image is loaded
    const currentUrl = new URL(this.windowRef.nativeWindow.location.href);
    const measurementsParam = currentUrl.searchParams.get("measurements");
    const annotationsParam = currentUrl.searchParams.get("annotations");

    if (measurementsParam && !this.isMeasuringMode) {
      // Remove annotations parameter from URL if it exists
      if (annotationsParam) {
        currentUrl.searchParams.delete("annotations");
        // Update the URL without navigation
        this.windowRef.replaceState({}, currentUrl.toString());
      }

      // Signal measuring mode is requested (will be activated by the combineLatest logic)
      this.isMeasuringRequested$.next(true);

      // If annotation mode is active, disable it immediately
      if (this.isAnnotationMode) {
        this.isAnnotationMode = false;
      }

      // Try to activate immediately if we can
      if (this.isMatrixLoaded$.value && this.isFullscreenActive$.value) {
        this._activateMeasuringMode();
      } else {
        this.pendingMeasurementToolActivation = true;
      }
    }

    // Check if we have a pending activation request
    else if (this.pendingMeasurementToolActivation && !this.isMeasuringMode) {
      // Try to activate immediately if we can
      if (this.isMatrixLoaded$.value && this.isFullscreenActive$.value) {
        this._activateMeasuringMode();
        this.pendingMeasurementToolActivation = false;
      }
    }
    // Only handle annotations if measurement tool is not activated
    else if (annotationsParam && !this.isMeasuringMode && !this.isAnnotationMode) {
      // Activate annotation mode after a slight delay to ensure all components are ready
      this.utilsService.delay(100).subscribe(() => {
        // Enable annotation mode
        this.isAnnotationMode = true;
        // Enable editing - annotations are always editable in fullscreen
        this.annotationReadOnlyMode = false;
        this.changeDetectorRef.markForCheck();
      });
    }

    // Ensure change detection is triggered to update the UI
    this.changeDetectorRef.markForCheck();
  }

  setZoomPosition(position: Coord) {
    this.showZoomIndicator = this.zoomingEnabled;
    this._setZoomIndicatorTimeout();

    // Only disable coordinates display in lens mode
    if (this.enableLens && this.zoomingEnabled && this.showCoordinates) {
      this.showCoordinates = false;
      this.mouseCoords = null;
      this.mouseX = null;
      this.mouseY = null;
      this.changeDetectorRef.markForCheck();
    }
  }

  setZoomScroll(scroll: number) {
    // If the zoom is changing, clear any "measuring tool only available at default zoom" notifications
    if (Math.abs(this.zoomScroll - scroll) > 0.001 && this._measureZoomNotification) {
      this.popNotificationsService.clear(this._measureZoomNotification.toastId);
      this._measureZoomNotification = null;
    }

    this.zoomScroll = scroll;

    // Show zoom indicator when zooming is enabled or when we're not at the default zoom level
    this.showZoomIndicator = this.zoomingEnabled || !this._isAtDefaultZoom();

    this._setZoomIndicatorTimeout();
  }

  snapTo1x() {
    if (this.ngxImageZoom) {
      try {
        this.ngxImageZoom.setMagnification = 1;
        this.ngxImageZoom.zoomService.zoomOn({ offsetX: 0, offsetY: 0 } as MouseEvent);
      } catch (e) {
        // Error in snapTo1x: e
      }
    }
  }

  @HostListener("document:keyup.escape", ["$event"])
  hide(
    event: MouseEvent | KeyboardEvent,
    options: {
      forceExitFullscreen?: boolean;
    } = {
      forceExitFullscreen: false
    }
  ): void {
    if (!this.isBrowser) {
      return;
    }

    if (event) {
      // Always prevent default to avoid browser's ESC behavior
      event.preventDefault();
    }

    if (!options || !options.forceExitFullscreen) {
      // Step 1: First check if we are in annotation mode
      if (this.isAnnotationMode) {
        // Exit annotation mode first
        this.onExitAnnotationMode();
        this.changeDetectorRef.markForCheck();
        return;
      }

      // Priority 2: Exit measuring mode if it's active
      if (this.isMeasuringMode) {
        this.toggleMeasuringMode(null);
        return;
      }

      // Priority 3: Handle open modals
      if (this.modalService.hasOpenModals()) {
        this.modalService.dismissAll();
        return;
      }

      // Priority 4: Close the kebab menu if it's open
      if (this.showKebabMenu) {
        this.showKebabMenu = false;
        this.changeDetectorRef.markForCheck();
        return;
      }

      // Clear mouse position to hide rulers
      this.mouseX = null;
      this.mouseY = null;
    }

    // Reset frozen state when hiding
    if (this.zoomFrozen) {
      this.zoomFrozen = false;
      this._restoreOriginalEventHandlers();
    }

    // Reset all state flags
    this.isFullscreenActive$.next(false);
    this.isMeasuringRequested$.next(false);
    this.isStaticImageLoaded$.next(false);
    this.isMatrixLoaded$.next(false);

    // Clean up all styles and classes
    this._cleanupHostStyles();

    // Clean up URL parameters when exiting
    if (this.isBrowser) {
      try {
        // Get the current URL
        const location = this.windowRef.nativeWindow.location;
        const currentUrl = new URL(location.href);

        // Remove the measurements parameter if it exists
        if (currentUrl.searchParams.has("measurements")) {
          currentUrl.searchParams.delete("measurements");

          // Update the URL without navigation
          this.windowRef.replaceState({}, currentUrl.toString());
        }
      } catch (e) {
        console.error("Error removing measurements parameter from URL:", e);
      }
    }

    this.popNotificationsService.clear();

    // When exiting fullscreen view completely, we should reset to the saved annotations
    // First clear all annotations, then restore saved ones if available
    this.annotationService.clearAllAnnotations();

    // If we have a revision with saved annotations, restore them
    if (this.revision && this.revision.annotations && this.revision.annotations.trim() !== "") {
      try {
        // Load the saved annotations from the revision
        this.annotationService.loadFromJsonString(this.revision.annotations);
      } catch (e) {
        console.error("Error restoring saved annotations:", e);
      }
    }

    // If annotations were saved in this session, reload the image data before hiding
    this.store$.dispatch(new HideFullscreenImage());
  }

  @HostListener("window:keyup.z", ["$event"])
  toggleZoomLensMode(event: KeyboardEvent): void {
    if (!this.isBrowser) {
      return;
    }

    // Don't interfere with input fields
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement ||
      (event.target instanceof HTMLDivElement && event.target.hasAttribute("contenteditable"))
    ) {
      return;
    }

    // Do nothing if the component is not being shown, in touch mode, or in measuring mode
    if (!this.show || this.touchMode || this.isMeasuringMode) {
      return;
    }

    // Toggle lens mode using existing method
    this.toggleEnableLens(null);
  }

  @HostListener("window:keyup.c", ["$event"])
  toggleCoordinatesShortcut(event: KeyboardEvent): void {
    if (!this.isBrowser) {
      return;
    }

    // Don't interfere with input fields
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement ||
      (event.target instanceof HTMLDivElement && event.target.hasAttribute("contenteditable"))
    ) {
      return;
    }

    // Do nothing if the component is not being shown, in measuring mode, or if the image doesn't have an advanced solution
    if (!this.show || !this.hasAdvancedSolution || !this.hasAdvancedSolutionMatrix) {
      this.popNotificationsService.info(this.translateService.instant("Coordinates are not available for this image."));
      return;
    }

    // Toggle coordinates using existing method
    this.toggleCoordinates();
  }

  @HostListener("window:keyup.d", ["$event"])
  toggleMeasuringModeShortcut(event: KeyboardEvent): void {
    if (!this.isBrowser) {
      return;
    }

    // Don't interfere with input fields
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement ||
      (event.target instanceof HTMLDivElement && event.target.hasAttribute("contenteditable"))
    ) {
      return;
    }

    // Do nothing if the component is not being shown or if it's a GIF
    if (!this.show || this.isGif) {
      return;
    }

    // Check if the image has a valid solution matrix before proceeding
    if (!this.hasAdvancedSolutionMatrix) {
      this.popNotificationsService.error(
        this.translateService.instant("The measuring tool is only available on images solved with PixInsight.")
      );
      return;
    }

    // Don't allow measuring when zoomed in - only at default zoom level (fit to window)
    if (!this.isMeasuringMode && !this._isAtDefaultZoom()) {
      // Store the notification reference, so we can clear it when zoom changes
      this._measureZoomNotification = this.popNotificationsService.warning(
        this.translateService.instant("Measuring is only available at the default zoom level (fit to window).")
      );
      return;
    }

    // Toggle measuring mode using existing method
    this.toggleMeasuringMode(event as unknown as MouseEvent);
  }

  // Reset zoom completely - return to initial non-zoomed state
  resetToDefaultZoom(): void {
    // Handle touch mode and mouse mode differently
    if (this.touchMode) {
      // For touch mode, fully reset the touch zoom using TouchZoomService
      this.touchZoom.reset();

      // Reset any touch zoom indicators
      // Note: actualTouchZoom should now be tracked by the service
    } else if (this.ngxImageZoom && this.ngxImageZoom.zoomService) {
      // For mouse mode, completely disable zooming and reset all zoom state

      // Turn off zoom completely
      this.ngxImageZoom.zoomService.zoomOff();

      // Reset all zoom related properties to initial values
      this.ngxImageZoom.zoomService.zoomingEnabled = false;
      this.ngxImageZoom.zoomService.magnification = 1;

      // Clear any measuring zoom notifications
      if (this._measureZoomNotification) {
        this.popNotificationsService.clear(this._measureZoomNotification.toastId);
        this._measureZoomNotification = null;
      }

      // Reset zoom indicators/display
      this.setZoomScroll(1);

      // Reset lens/thumb image position to ensure a clean state
      const thumbElement = this.ngxImageZoomEl?.nativeElement?.querySelector(".ngxImageZoomThumbnail");
      if (thumbElement) {
        thumbElement.style.transform = "translate(0px, 0px)";
      }

      // Hide any active lens
      const lensElement = this.ngxImageZoomEl?.nativeElement?.querySelector(".ngxImageZoomLens");
      if (lensElement) {
        lensElement.style.display = "none";
      }
    }

    // Clear any measuring tool state
    if (this.isMeasuringMode) {
      this.resetMeasurement();
    }

    // Force change detection to ensure UI updates
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Handle exiting annotation mode - when exiting, the canvas is already in the DOM
   * because we keep both the static image and canvas rendered at all times
   */
  onExitAnnotationMode(): void {
    // Set annotation mode to false
    this.isAnnotationMode = false;

    // Reset to read-only mode for next time
    this.annotationReadOnlyMode = true;

    // Reload image data to ensure we have the latest annotations
    if (this.revision?.pk) {
      // Get the latest image data from store (if any annotations were saved)
      this.store$
        .pipe(
          select(selectImage, this.id),
          filter(image => !!image),
          take(1)
        )
        .subscribe(image => {
          // Update our local reference with fresh data from the store
          if (image) {
            // Find the correct revision from the updated image
            // Start with the original image (which is the "revision 0")
            let updatedRevision: ImageInterface | ImageRevisionInterface = null;

            if (this.revisionLabel === ORIGINAL_REVISION_LABEL) {
              // If we're looking for the original revision, use the image itself
              updatedRevision = image;
            } else if (this.revisionLabel === FINAL_REVISION_LABEL) {
              // If we're looking for the final revision, use the image if it's marked as final
              if (image.isFinal) {
                updatedRevision = image;
              } else {
                // Otherwise find the revision marked as final
                updatedRevision = image.revisions.find(rev => rev.isFinal);
              }
            } else {
              // For any other revision label, find it by label
              updatedRevision = image.revisions.find(rev => rev.label === this.revisionLabel);
            }

            if (updatedRevision) {
              // Update the revision with the latest data
              this.revision = updatedRevision;
            }
          }
        });
    }

    this.changeDetectorRef.markForCheck();
  }

  onAnnotationsSaved(annotationsJson: string): void {
    // Update the revision with the new annotations
    if (this.revision) {
      this.revision = {
        ...this.revision,
        annotations: annotationsJson
      };

      // Force change detection
      this.changeDetectorRef.markForCheck();
    }
  }

  @HostListener("window:keyup.n", ["$event"])
  toggleAnnotationMode(event: KeyboardEvent | MouseEvent): void {
    if (!this.isBrowser) {
      return;
    }

    // Don't interfere with input fields if this is a keyboard event
    if (event instanceof KeyboardEvent) {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        (event.target instanceof HTMLDivElement && event.target.hasAttribute("contenteditable"))
      ) {
        return;
      }
    }

    // Do nothing if the component is not being shown, in measuring mode, or if the image is a GIF
    if (!this.show || this.isMeasuringMode || this.isGif) {
      return;
    }

    // Simply toggle annotation mode
    this.isAnnotationMode = !this.isAnnotationMode;

    // Enable editing in fullscreen view - annotations are always editable in fullscreen
    this.annotationReadOnlyMode = false;

    // Force change detection
    this.changeDetectorRef.markForCheck();
  }

  @HostListener("window:keyup.f", ["$event"])
  toggleZoomFreeze(event: KeyboardEvent | MouseEvent): void {
    if (!this.isBrowser) {
      return;
    }

    // Don't interfere with input fields
    if (event instanceof KeyboardEvent) {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        (event.target instanceof HTMLDivElement && event.target.hasAttribute("contenteditable"))
      ) {
        return;
      }
    }

    // Do nothing if the component is not being shown, in measuring mode, or in annotation mode
    if (!this.show || this.isMeasuringMode || this.isAnnotationMode) {
      return;
    }

    // Only work in non-touch mode with active zooming
    if (!this.zoomingEnabled || this.touchMode || this.isVeryLargeImage) {
      // Show feedback for why F key doesn't work
      if (this.touchMode) {
        this.popNotificationsService.info(
          this.translateService.instant("Zoom freezing is only available in mouse mode.")
        );
      } else if (this.isVeryLargeImage) {
        this.popNotificationsService.info(
          this.translateService.instant("Zoom freezing is not available for very large images.")
        );
      } else if (!this.zoomingEnabled) {
        // Store the toast reference so we can clear this specific notification when zoom is activated
        this._zoomActivationNotification = this.popNotificationsService.info(
          this.translateService.instant("Activate zoom first before freezing (click or scroll on image).")
        );
      }
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // Toggle frozen state
    this.zoomFrozen = !this.zoomFrozen;

    if (this.zoomFrozen) {
      // Directly remove the mouse events from the ngx-image-zoom component
      this._deregisterZoomEvents();
    } else {
      // Re-register the events to enable normal behavior
      this._registerZoomEvents();
    }

    this.changeDetectorRef.markForCheck();
  }

  // Handle wheel events on the component and proxy them to ngx-image-zoom
  /**
   * Handle wheel events and apply appropriate zoom behavior
   */
  protected onGlobalWheel(event: WheelEvent): void {
    // Block wheel events during measuring mode or annotation mode
    if (this.isMeasuringMode || this.isAnnotationMode) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // If zoom is disabled by image owner, prevent zooming
    if (!this.allowZoom) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // If the event has ctrlKey, it's a pinch gesture in Firefox or zoom in other browsers
    // Always prevent browser zoom
    if (event.ctrlKey) {
      event.preventDefault();
      event.stopPropagation();

      // Handle Firefox pinch gestures directly here
      // This ensures the component's wheel events get proxied to ngx-image-zoom
      if (!this.touchMode && this.ngxImageZoom && !this.isVeryLargeImage && !this.zoomFrozen) {
        this._handleFirefoxPinchZoom(event);
      }
      return;
    }

    // Don't handle zoom events if frozen
    if (this.zoomFrozen) {
      return;
    }

    // Only proxy the event if we have the zoom component and we're not in touch mode
    if (!this.touchMode && this.ngxImageZoom && !this.isVeryLargeImage) {
      // First check if we need to activate zoom
      if (!this.zoomingEnabled) {
        // Auto-dismiss the scroll instruction when user first activates zoom via scroll
        if (!this.isInstructionDismissed("scrollToMagnify")) {
          this.dismissInstruction("scrollToMagnify");
        }

        // If we're not zoomed yet, activate zoom at event position
        this.ngxImageZoom.zoomService.magnification = this.ngxImageZoom.zoomService.minZoomRatio;
        this.ngxImageZoom.zoomService.zoomOn(event);

        // Clear specific "Activate zoom first" notification if it exists
        if (this._zoomActivationNotification) {
          // Extract toast ID - ActiveToast has a toastId property of type number
          if (typeof this._zoomActivationNotification !== "string" && this._zoomActivationNotification.toastId) {
            this.popNotificationsService.clear(this._zoomActivationNotification.toastId);
          }
          this._zoomActivationNotification = null;
        }

        this.changeDetectorRef.markForCheck();
        return;
      }

      // If already zoomed, modify the zoom level based on wheel delta
      if (this.zoomingEnabled) {
        // Get current values
        const currentMag = this.ngxImageZoom.zoomService.magnification;
        const minRatio = this.ngxImageZoom.zoomService.minZoomRatio || 1;
        const maxRatio = this.ngxImageZoom.zoomService.maxZoomRatio || 2;
        const stepSize = 0.05; // Default step size

        // For normal wheel events, use deltaY with opposite sign (up = zoom in, down = zoom out)
        const delta = -event.deltaY / 100; // Normalize regular wheel delta

        // Calculate new magnification
        let newMag = currentMag;
        if (delta > 0) {
          // Scroll up - zoom in
          newMag = Math.min(currentMag + stepSize, maxRatio);
        } else if (delta < 0) {
          // Scroll down - zoom out
          newMag = Math.max(currentMag - stepSize, minRatio);
        }

        // Update magnification if changed
        if (newMag !== currentMag) {
          // Auto-dismiss the scroll instruction when user actually performs scroll-to-zoom
          if (!this.isInstructionDismissed("scrollToMagnify")) {
            this.dismissInstruction("scrollToMagnify");
          }

          this.ngxImageZoom.zoomService.magnification = newMag;

          // Update calculations
          this.ngxImageZoom.zoomService.calculateRatio();
          this.ngxImageZoom.zoomService.calculateZoomPosition(event);

          // Update zoom indicator
          this.setZoomScroll(newMag);
          this.changeDetectorRef.markForCheck();
        }
      }
    }
  }

  // Handle mouse move events on the component and proxy them to ngx-image-zoom
  protected onGlobalMouseMove(event: MouseEvent): void {
    // If in measuring mode, and we've already placed the first point,
    // update line drawing position but allow coordinate calculation to continue
    if (this.isMeasuringMode && this.measureStartPoint) {
      // Update mouse position for line drawing
      this.mouseX = event.clientX;
      this.mouseY = event.clientY;

      // Don't trigger zoom functionality, but continue to next section to update coordinates
      // This allows coordinates to be updated in the display box while placing the second point
    }

    // We need to ensure the revision is loaded before calculating coordinates
    if (!this.revision) {
      return;
    }

    // Don't update position if frozen
    if (this.zoomFrozen) {
      return;
    }

    // Don't update position if touch mode is active
    if (this.touchMode) {
      return;
    }

    // Update mouse position for crosshair rulers and coordinate display
    const imageElement = this.ngxImageZoomEl?.nativeElement?.querySelector(".ngxImageZoomContainer img");

    if (imageElement) {
      // Get fresh dimensions each time
      const imageRect = imageElement.getBoundingClientRect();

      // Always store the mouse position for crosshairs if coordinates are enabled
      if (this.showCoordinates) {
        this.mouseX = event.clientX;
        this.mouseY = event.clientY;

        // Check if mouse is within bounds of the actual image
        if (
          event.clientX >= imageRect.left &&
          event.clientX <= imageRect.right &&
          event.clientY >= imageRect.top &&
          event.clientY <= imageRect.bottom
        ) {
          // Coordinates calculation removed from here
        } else {
          // Hide coordinate display when mouse is outside the image
          this.mouseCoords = null;
        }
      } else {
        // Coordinates are disabled, clear all values
        this.mouseX = null;
        this.mouseY = null;
        this.mouseCoords = null;
      }

      this.changeDetectorRef.markForCheck();
    }

    // Only proxy if we have the zoom component, are in mouse mode, not in measuring mode, and are currently zooming
    if (
      !this.touchMode &&
      this.ngxImageZoom &&
      this.zoomingEnabled &&
      !this.isVeryLargeImage &&
      !this.isMeasuringMode &&
      !this.isAnnotationMode
    ) {
      // We need to convert global coordinates to coordinates relative to the image
      // Find the image and its position
      const zoomContainer = this.ngxImageZoomEl.nativeElement.querySelector(".ngxImageZoomContainer");
      if (!zoomContainer) {
        return;
      }

      // Get container's position
      const rect = zoomContainer.getBoundingClientRect();

      // Check if mouse is within the bounds of the zoom container
      if (
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom
      ) {
        // Convert global coordinates to relative coordinates
        const relativeEvent = {
          ...event,
          offsetX: event.clientX - rect.left,
          offsetY: event.clientY - rect.top
        };

        // Update the zoom position
        this.ngxImageZoom.zoomService.calculateZoomPosition(relativeEvent);
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  protected toggleEnableLens(event: MouseEvent = null): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();

      // Hide any tooltips before toggling
      this._clearTooltips();
    }

    const wasInLensMode = this.enableLens;
    this.enableLens = !this.enableLens;
    this.cookieService.put(this.LENS_ENABLED_COOKIE_NAME, this.enableLens.toString());

    if (this.enableLens) {
      // Entering lens mode
      this._setZoomLensSize();

      // Turn off coordinates when lens mode is activated
      if (this.showCoordinates) {
        this.showCoordinates = false;
        this.mouseCoords = null;
        this.mouseX = null;
        this.mouseY = null;
        this.changeDetectorRef.markForCheck();
      }
    } else if (wasInLensMode) {
      // Exiting lens mode - clear any notifications that might be related to lens mode
      this.popNotificationsService.clear();
    }
  }

  protected setMouseOverUIElement(isOver: boolean): void {
    this.isMouseOverUIElement = isOver;
    this.changeDetectorRef.markForCheck();
  }

  protected showShortcutsDialog(event: MouseEvent): void {
    // Prevent the event from propagating
    event.preventDefault();
    event.stopPropagation();

    // Hide any tooltips
    document.querySelectorAll(".tooltip").forEach(tooltip => {
      tooltip.remove();
    });

    // Close the kebab menu
    this.showKebabMenu = false;

    // Toggle the shortcuts overlay
    this.showKeyboardShortcutsOverlay = true;

    this.changeDetectorRef.markForCheck();
  }

  protected hideShortcutsOverlay(): void {
    this.showKeyboardShortcutsOverlay = false;
    this.changeDetectorRef.markForCheck();
  }

  protected toggleKebabMenu(event: MouseEvent): void {
    // Prevent the event from propagating to avoid triggering other click handlers
    event.preventDefault();
    event.stopPropagation();

    // Hide any tooltips before toggling
    this._clearTooltips();

    this.showKebabMenu = !this.showKebabMenu;

    // If opening the menu, add a click event listener to close it when clicking outside
    if (this.showKebabMenu && this.isBrowser) {
      this.utilsService.delay(0).subscribe(() => {
        const closeMenuHandler = (e: MouseEvent) => {
          // Check if the click was outside the menu
          const kebabContainer = (event.target as HTMLElement).closest(".kebab-menu-container");
          const clickedInsideMenu = kebabContainer && kebabContainer.contains(e.target as Node);

          if (!clickedInsideMenu) {
            this.showKebabMenu = false;
            this.changeDetectorRef.markForCheck();
            document.removeEventListener("click", closeMenuHandler);
          }
        };

        document.addEventListener("click", closeMenuHandler);
      });
    }

    this.changeDetectorRef.markForCheck();
  }

  protected toggleCoordinates(): void {
    // Hide any tooltips before toggling
    this._clearTooltips();

    // If in lens mode, show notification and don't toggle
    if (this.enableLens && this.zoomingEnabled) {
      this.popNotificationsService.warning(
        this.translateService.instant("Coordinates are not available in lens mode.")
      );
      return;
    }

    this.showCoordinates = !this.showCoordinates;
    this.cookieService.put(this.COORDINATES_ENABLED_COOKIE_NAME, this.showCoordinates.toString());

    // If coordinates are turned off, hide any currently displayed values
    if (!this.showCoordinates) {
      this.mouseCoords = null;
      this.mouseX = null;
      this.mouseY = null;
    } else if (this.mouseX !== null && this.mouseY !== null) {
      // If coordinates are turned on and we have a current mouse position, recalculate
      const lastMouseEvent = this.windowRef.nativeWindow.event as MouseEvent;
      if (lastMouseEvent && lastMouseEvent.type === "mousemove") {
        this.onGlobalMouseMove(lastMouseEvent);
      }
    }
    this.changeDetectorRef.markForCheck();
  }

  protected toggleMeasuringMode(event?: MouseEvent, fromSharedUrl: boolean = false): void {
    // Prevent the event from propagating
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Hide any tooltips before toggling
    this._clearTooltips();

    // If measuring mode is currently on, just turn it off
    if (this.isMeasuringMode) {
      // Disable measuring mode
      this.isMeasuringMode = false;
      // Reset the request state
      this.isMeasuringRequested$.next(false);

      // Reset mouse position tracking
      this.mouseX = null;
      this.mouseY = null;

      // Clean up URL parameters when exiting measuring mode
      if (this.isBrowser) {
        try {
          // Get the current URL
          const location = this.windowRef.nativeWindow.location;
          const currentUrl = new URL(location.href);

          // Remove the measurements parameter if it exists
          if (currentUrl.searchParams.has("measurements")) {
            currentUrl.searchParams.delete("measurements");

            // Update the URL without navigation
            this.windowRef.replaceState({}, currentUrl.toString());
          }
        } catch (e) {
          console.error("Error removing measurements parameter from URL:", e);
        }
      }

      this.changeDetectorRef.markForCheck();
      return;
    }

    // When turning on the measuring mode, check if the image has a valid solution matrix
    // If we're still loading the solution matrix, wait for it to complete
    if (this.loadingAdvancedSolutionMatrix) {
      // Show loading indicator
      const loadingToast = this.popNotificationsService.info(
        this.translateService.instant("Loading solution data..."),
        null,
        { disableTimeOut: true }
      );

      // Create a polling interval to check when loading completes
      interval(200)
        .pipe(
          filter(() => !this.loadingAdvancedSolutionMatrix),
          take(1),
          takeUntil(this.destroyed$)
        )
        .subscribe(() => {
          // Hide loading indicator
          if (loadingToast) {
            this.popNotificationsService.clear(loadingToast.toastId);
          }

          // Now check if we have a valid solution matrix
          if (this.hasAdvancedSolutionMatrix) {
            // Request measuring mode activation
            this.isMeasuringRequested$.next(true);
            // Activate immediately since matrix is loaded
            this._activateMeasuringMode();
          } else {
            // Show error notification
            this.popNotificationsService.error(
              this.translateService.instant("The measuring tool is only available on images solved with PixInsight.")
            );
          }
        });

      return;
    }

    // Regular check if not loading
    if (!this.hasAdvancedSolutionMatrix) {
      this.popNotificationsService.error(
        this.translateService.instant("The measuring tool is only available on images solved with PixInsight.")
      );
      return;
    }

    // All checks passed, request measuring mode activation
    this.isMeasuringRequested$.next(true);

    // Check if we need to first load the static image
    if (!this.isStaticImageLoaded) {
      this.pendingMeasurementToolActivation = true;
      // We'll activate measuring mode once the static image loads
      this.changeDetectorRef.markForCheck();
    } else {
      // Static image is already loaded, activate immediately
      this._activateMeasuringMode();
      this.changeDetectorRef.markForCheck();
    }
  }

  protected resetMeasurement(): void {
    this.measureStartPoint = null;
    this.measureEndPoint = null;
    this.measureDistance = null;
    this.changeDetectorRef.markForCheck();
  }

  protected clearCoordinates(): void {
    if (this.showCoordinates) {
      this.mouseX = null;
      this.mouseY = null;
      this.mouseCoords = null;
      this.changeDetectorRef.markForCheck();
    }
  }

  // Handler for coordinate changes from the tracker
  protected onCoordsChange(coords: any, event: MouseEvent): void {
    this.mouseCoords = coords;
    if (event) {
      this.mouseX = event.clientX;
      this.mouseY = event.clientY;
    }
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Safely initialize touch zoom functionality with proper null checking
   * @private
   */
  private _tryInitTouchZoom(delayMs: number = 100): void {
    // Wait for thumbnails to load if they're not yet available
    if (!this.realThumbnailUnsafeUrl) {
      if (!this._eagerLoadingSubscription && !this.hdThumbnail && !this.realThumbnail) {
        this._initThumbnailSubscriptions();
      }
    }

    // Schedule initialization with a delay
    this.utilsService.delay(delayMs).subscribe(() => {
      // Only proceed if we have all required elements
      if (
        this.touchRealCanvas?.nativeElement &&
        this.touchRealContainer?.nativeElement &&
        this.realThumbnailUnsafeUrl
      ) {
        this.touchZoom.init(
          this.touchRealCanvas.nativeElement,
          this.touchRealContainer.nativeElement,
          this.realThumbnailUnsafeUrl,
          this.changeDetectorRef,
          this.naturalWidth,
          this.naturalHeight,
          this.maxZoom
        );
      } else if (this.touchMode) {
        // If still in touch mode but elements not ready, retry with a longer delay
        this._tryInitTouchZoom(Math.min(delayMs * 1.5, 500)); // Increase delay but cap at 500ms
      }
      this.changeDetectorRef.markForCheck();
    });
  }

  protected setTouchMouseMode(touch: boolean): void {
    // Hide any tooltips before toggling
    this._clearTooltips();

    // Never allow touch mode for GIFs
    if (this.isGif && touch) {
      return;
    }

    this.touchMode = touch;
    this.cookieService.put(this.TOUCH_OR_MOUSE_MODE_COOKIE_NAME, this.touchMode ? "touch" : "mouse");

    // For touch mode, initialize canvas
    if (this.touchMode) {
      // Centralized initialization that handles all cases
      this._tryInitTouchZoom(10);
    } else {
      // Not in touch mode, reset canvas
      this._resetCanvas();
    }
  }

  protected onPinchStart(event: HammerInput): void {
    // Skip if in annotation mode
    if (this.isAnnotationMode) {
      return;
    }
    this.touchZoom.onPinchStart(event);
  }

  protected onPinchMove(event: HammerInput): void {
    // Skip if in annotation mode
    if (this.isAnnotationMode) {
      return;
    }

    // Auto-dismiss the pinch instruction when user actually performs pinch gesture
    if (!this.isInstructionDismissed("pinchAndZoom")) {
      this.dismissInstruction("pinchAndZoom");
    }

    this.touchZoom.onPinchMove(event);
  }

  protected onPinchEnd(): void {
    // Skip if in annotation mode
    if (this.isAnnotationMode) {
      return;
    }
    this.touchZoom.onPinchEnd();
  }

  protected onPanStart(event: HammerInput): void {
    // Skip if in annotation mode
    if (this.isAnnotationMode) {
      return;
    }
    this.touchZoom.onPanStart(event);
  }

  protected onPanMove(event: HammerInput): void {
    // Skip if in annotation mode
    if (this.isAnnotationMode) {
      return;
    }
    this.touchZoom.onPanMove(event);
  }

  protected onPanEnd(event: HammerInput) {
    // Skip if in annotation mode
    if (this.isAnnotationMode) {
      return;
    }
    this.touchZoom.onPanEnd(event);
  }

  protected initTouchZoom(): void {
    // Check if required elements are missing
    if (
      !this.touchRealCanvas ||
      !this.touchRealContainer ||
      !this.realThumbnailUnsafeUrl ||
      !this.touchMode ||
      this.isGif ||
      !this.allowReal ||
      !this.realThumbnail
    ) {
      // If elements aren't ready yet, retry with a delay (max 10 retries)
      if (this._initTouchZoomRetryCount < 10) {
        this._initTouchZoomRetryCount++;
        this.utilsService.delay(100).subscribe(() => {
          this.initTouchZoom();
          this.changeDetectorRef.markForCheck();
        });
      } else {
        this._initTouchZoomRetryCount = 0; // Reset counter after max retries
      }
      return;
    }

    // Reset retry counter on successful initialization
    this._initTouchZoomRetryCount = 0;

    // Don't initialize if already loading
    if (this.canvasLoading) {
      return;
    }

    // Set canvas loading state
    this.canvasLoading = true;

    // Initialize the touch zoom service with the canvas and container
    this.touchZoom.init(
      this.touchRealCanvas.nativeElement,
      this.touchRealContainer.nativeElement,
      this.realThumbnailUnsafeUrl,
      this.changeDetectorRef,
      this.revision?.w, // natural width
      this.revision?.h, // natural height
      this.maxZoom
    );

    // Subscribe to first render completion to update loading progress
    this.touchZoom.firstRender$.pipe(take(1)).subscribe(() => {
      this.canvasLoading = false;
      this._realLoadingProgressSubject.next(100);
      this.changeDetectorRef.markForCheck();
    });
  }

  protected onDoubleTap(event: HammerInput): void {
    // Skip if in annotation mode
    if (this.isAnnotationMode) {
      return;
    }
    this.touchZoom.onDoubleTap(event);
  }

  /**
   * Checks if we can swipe to close the viewer
   * Only allow swipe-down when the image is not zoomed in beyond fit-to-screen
   */
  protected canSwipeToClose(): boolean {
    // For GIFs we can always swipe to close (no zoom)
    if (this.isGif) {
      return true;
    }

    // For very large images we can always swipe to close (no interactive zoom)
    if (this.isVeryLargeImage) {
      return true;
    }

    if (this.touchMode) {
      // In touch mode, we can only swipe when the touchScale is 1 (fully zoomed out)
      return this.touchScale <= 1;
    } else {
      // In mouse mode, we can only swipe when the zoom is at 1x
      return !this.zoomingEnabled || this.zoomScroll <= 1;
    }
  }

  /**
   * Handle touch start for swipe-down gesture
   */
  protected onTouchStart(event: TouchEvent): void {
    if (!this.show) {
      return;
    }

    // Completely reset state variables to ensure we can start a new swipe
    this.isSwiping.value = false;
    this.swipeProgress.value = 0;
    this.touchStartY.value = 0;
    this.touchCurrentY.value = 0;
    this.touchPreviousY.value = 0;
    this.swipeDirectionDown.value = true;

    // Remove any swipe-related classes that might be lingering
    this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-animating");
    this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-animate");
    this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-return-to-normal");

    // Remove any inline styles that might be preventing gestures
    this.renderer.removeStyle(this.hostElementRef.nativeElement, "transition");
    this.renderer.removeStyle(this.hostElementRef.nativeElement, "transform");
    this.renderer.removeStyle(this.hostElementRef.nativeElement, "opacity");
    this.renderer.removeStyle(this.hostElementRef.nativeElement, "will-change");

    // Set initial opacity to match the show state
    if (this.show) {
      this.renderer.setStyle(this.hostElementRef.nativeElement, "opacity", "1");
    }

    // Initialize the new touch gesture
    this.swipeDownService.handleTouchStart(
      event,
      this.touchStartY,
      this.touchCurrentY,
      this.touchPreviousY,
      this.swipeDirectionDown
    );
  }

  /**
   * Handle touch move for swipe-down gesture
   */
  protected onTouchMove(event: TouchEvent): void {
    const wasSwipingBefore = this.isSwiping.value;

    // Call the service handler
    const isSwipingNow = this.swipeDownService.handleTouchMove(
      event,
      this.touchStartY,
      this.touchCurrentY,
      this.touchPreviousY,
      this.swipeDirectionDown,
      this.isSwiping,
      this.swipeProgress,
      this.swipeThreshold,
      this.hostElementRef,
      this.renderer,
      () => this.canSwipeToClose()
    );

    // If we just started swiping, add the class for background animation
    if (!wasSwipingBefore && isSwipingNow && typeof document !== "undefined") {
      document.body.classList.add("image-viewer-closing");
    }

    // If we were swiping but now stopped (user moved back up), remove the class
    if (wasSwipingBefore && !isSwipingNow && typeof document !== "undefined") {
      document.body.classList.remove("image-viewer-closing");

      // Add return animation class - this is the key to the bounce-back behavior
      this.renderer.addClass(this.hostElementRef.nativeElement, "swipe-to-close-animating");
      this.renderer.addClass(this.hostElementRef.nativeElement, "swipe-to-close-return-to-normal");

      // Set up a listener for animation end to clean up classes
      const onAnimationEnd = (event: any) => {
        if (event.animationName === "return-to-normal") {
          // Remove the animation classes when complete
          this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-animating");
          this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-return-to-normal");

          // Reset transform explicitly to ensure no residual transform remains
          this.renderer.setStyle(this.hostElementRef.nativeElement, "transform", "translateY(0) scale(1)");

          // Clean up the event listener
          this.hostElementRef.nativeElement.removeEventListener("animationend", onAnimationEnd);
        }
      };

      // Add the event listener
      this.hostElementRef.nativeElement.addEventListener("animationend", onAnimationEnd);

      // Safety timeout in case animation end doesn't fire
      this.utilsService.delay(300).subscribe(() => {
        // Also clean up styles here as a fallback
        this.renderer.setStyle(this.hostElementRef.nativeElement, "transform", "translateY(0) scale(1)");
      });
    }
  }

  /**
   * Handle touch end for swipe-down gesture
   */
  protected onTouchEnd(event: TouchEvent): void {
    // Only process if we're swiping
    if (!this.isSwiping.value) {
      return;
    }

    // Add class for background animation - the service will handle cancel detection
    if (typeof document !== "undefined") {
      document.body.classList.add("image-viewer-closing");
    }

    // Get the current swipe delta
    const deltaY = this.touchCurrentY.value - this.touchStartY.value;

    // Check if swipe threshold has been met and if the swipe is downward
    const thresholdMet = deltaY >= this.swipeThreshold && this.swipeDirectionDown.value;

    if (thresholdMet) {
      // Add animation classes for close animation
      this.renderer.addClass(this.hostElementRef.nativeElement, "swipe-to-close-animating");
      this.renderer.addClass(this.hostElementRef.nativeElement, "swipe-to-close-animate");
      // Keep track of whether we've already handled the animation completion
      let animationHandled = false;
      // Listen for animation end
      const onAnimationEnd = (event: any) => {
        if (event.animationName === "swipe-to-close" && !animationHandled) {
          animationHandled = true;

          // Set opacity to 0 immediately to prevent flashing
          this.renderer.setStyle(this.hostElementRef.nativeElement, "opacity", "0");

          // Remove classes first
          this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-animating");
          this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-animate");

          // Important: Hide component with slight delay to prevent flashing
          // This ensures styles are applied before component removal
          this.utilsService.delay(50).subscribe(() => {
            this.hide(null);
          });

          // Remove event listener
          this.hostElementRef.nativeElement.removeEventListener("animationend", onAnimationEnd);
        }
      };

      // Add the event listener
      this.hostElementRef.nativeElement.addEventListener("animationend", onAnimationEnd);

      // Safety timeout in case animation end doesn't fire
      this.utilsService.delay(400).subscribe(() => {
        if (!animationHandled) {
          animationHandled = true;

          // Same approach as animation end handler
          this.renderer.setStyle(this.hostElementRef.nativeElement, "opacity", "0");
          this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-animating");
          this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-animate");

          this.utilsService.delay(50).subscribe(() => {
            this.hide(null);
          });
        }
      });

      // Reset swipe state immediately
      this.isSwiping.value = false;
      this.swipeProgress.value = 0;
    } else {
      // For canceled swipes, use the same approach as in onTouchMove
      this.renderer.addClass(this.hostElementRef.nativeElement, "swipe-to-close-animating");
      this.renderer.addClass(this.hostElementRef.nativeElement, "swipe-to-close-return-to-normal");

      // Listen for animation end
      const onAnimationEnd = (event: any) => {
        if (event.animationName === "return-to-normal") {
          // Remove animation classes
          this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-animating");
          this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-return-to-normal");

          // Reset transform explicitly to ensure no residual transform remains
          this.renderer.setStyle(this.hostElementRef.nativeElement, "transform", "translateY(0) scale(1)");

          // Remove event listener
          this.hostElementRef.nativeElement.removeEventListener("animationend", onAnimationEnd);

          // Also ensure touch state is completely reset
          this.touchStartY.value = 0;
          this.touchCurrentY.value = 0;
          this.touchPreviousY.value = 0;
        }
      };

      // Add the event listener
      this.hostElementRef.nativeElement.addEventListener("animationend", onAnimationEnd);

      // Safety timeout in case animation end doesn't fire
      this.utilsService.delay(300).subscribe(() => {
        // Also clean up styles here as a fallback
        this.renderer.setStyle(this.hostElementRef.nativeElement, "transform", "translateY(0) scale(1)");

        // Ensure touch state is reset even if animation end doesn't fire
        this.touchStartY.value = 0;
        this.touchCurrentY.value = 0;
        this.touchPreviousY.value = 0;
      });

      // Reset swipe state immediately
      this.isSwiping.value = false;
      this.swipeProgress.value = 0;

      // Remove the closing class from the body
      if (typeof document !== "undefined") {
        document.body.classList.remove("image-viewer-closing");
      }
    }
  }

  // Update the image element reference - called after zoom initialization
  private _updateImageElementReference(): void {
    if (this.ngxImageZoomEl?.nativeElement) {
      this.imageElementForCoordinateTracker = this.ngxImageZoomEl.nativeElement.querySelector(".ngxImageZoomContainer");
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Clean up all inline styles and animation classes that might be present on the host element
   * This is important for proper component display/hiding
   */
  private _cleanupHostStyles(): void {
    this.renderer.removeStyle(this.hostElementRef.nativeElement, "transform");
    this.renderer.removeStyle(this.hostElementRef.nativeElement, "opacity");
    this.renderer.removeStyle(this.hostElementRef.nativeElement, "transition");
    this.renderer.removeStyle(this.hostElementRef.nativeElement, "will-change");
    this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-animating");
    this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-animate");
    this.renderer.removeClass(this.hostElementRef.nativeElement, "swipe-to-close-return-to-normal");

    // Remove any body classes that might have been added during swipe
    if (typeof document !== "undefined") {
      document.body.classList.remove("image-viewer-closing");
      document.body.classList.remove("fullscreen-image-viewer-active");
    }
  }

  /**
   * Check if the current zoom level is at the default (fit to window) level
   */
  private _isAtDefaultZoom(): boolean {
    if (
      !this.ngxImageZoom ||
      !this.ngxImageZoom.zoomService ||
      this.ngxImageZoom.zoomService.minZoomRatio === undefined
    ) {
      // If zoom service is not available or minZoomRatio is not initialized, we can't determine the default zoom
      return false;
    }

    // When component is initializing, zoomScroll might not be set yet
    if (this.zoomScroll === undefined || this.zoomScroll === null) {
      // During initialization, assume we're at default zoom if magnification is at or close to minZoomRatio
      if (this.ngxImageZoom.zoomService.magnification !== undefined) {
        return (
          Math.abs(this.ngxImageZoom.zoomService.magnification - this.ngxImageZoom.zoomService.minZoomRatio) <= 0.01
        );
      }
      // If even magnification is not set, assume we're at default zoom (initial state)
      return true;
    }

    // Get the minimum zoom ratio (fit to window)
    const minRatio = this.ngxImageZoom.zoomService.minZoomRatio;

    // Use a small threshold for floating point comparison with the minimum ratio
    return Math.abs(this.zoomScroll - minRatio) <= 0.01;
  }

  /**
   * Safely remove tooltips when we need to clear UI elements
   * This handles SSR by checking if we're in a browser environment
   */
  private _clearTooltips(): void {
    if (this.isBrowser) {
      document.querySelectorAll(".tooltip").forEach(tooltip => {
        tooltip.remove();
      });
    }
  }

  // Replace both mousemove and wheel handlers to completely freeze the zoom
  private _deregisterZoomEvents(): void {
    if (!this.ngxImageZoom || !(this.ngxImageZoom as any).zoomInstance) {
      return;
    }

    // Store the original handlers
    this._originalOnMouseMove = (this.ngxImageZoom as any).zoomInstance.onMouseMove;
    this._originalOnMouseWheel = (this.ngxImageZoom as any).zoomInstance.onMouseWheel;

    // Replace with empty handler - this freezes the zoom position
    (this.ngxImageZoom as any).zoomInstance.onMouseMove = (event: MouseEvent) => {
      // Do nothing - freezes the zoom position
    };

    // Block wheel events to prevent zooming while frozen
    (this.ngxImageZoom as any).zoomInstance.onMouseWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
      return false;
    };
  }

  // Helper method to restore all original event handlers
  private _restoreOriginalEventHandlers(): void {
    if (!this.ngxImageZoom || !(this.ngxImageZoom as any).zoomInstance) {
      return;
    }

    // Restore the original mouse move handler
    if (this._originalOnMouseMove) {
      (this.ngxImageZoom as any).zoomInstance.onMouseMove = this._originalOnMouseMove;
      this._originalOnMouseMove = null;
    }

    // Restore the original wheel handler
    if (this._originalOnMouseWheel) {
      (this.ngxImageZoom as any).zoomInstance.onMouseWheel = this._originalOnMouseWheel;
      this._originalOnMouseWheel = null;
    }
  }

  // Restore original event handlers
  private _registerZoomEvents(): void {
    this._restoreOriginalEventHandlers();
  }

  /**
   * Handle Firefox pinch-to-zoom gestures by updating magnification
   * @param event WheelEvent with ctrlKey for Firefox pinch gestures
   * @param syntheticEvent Optional synthetic event with proper coordinates
   */
  /**
   * Ensures that a solution matrix is loaded, with proper state tracking
   * This prevents race conditions between multiple components trying to load the same matrix
   */

  private _handleFirefoxPinchZoom(event: WheelEvent, syntheticEvent?: WheelEvent): void {
    // Always prevent browser zoom when using ctrl+wheel (Firefox pinch gesture)
    event.preventDefault();
    event.stopPropagation();

    // Only handle zoom operations if not frozen, not in measuring mode, not in annotation mode, and zoom component exists
    if (this.zoomFrozen || this.isMeasuringMode || this.isAnnotationMode || !this.ngxImageZoom?.zoomService) {
      return;
    }

    // Get current magnification settings
    const mag = this.ngxImageZoom.zoomService.magnification;
    const minRatio = this.ngxImageZoom.zoomService.minZoomRatio || 1;
    const maxRatio = this.ngxImageZoom.zoomService.maxZoomRatio || 2;

    // Get the actual deltas which will now work directly with pinch gestures
    const deltaY = event.deltaY;

    // Calculate zoom step - normalize Firefox touchpad pinch deltas
    // Firefox produces larger deltaY values with trackpad pinch gestures
    const step = 0.05 * (Math.abs(deltaY) / 20);

    // Calculate new magnification with correct direction
    // In Firefox, positive deltaY = pinch in (should zoom out)
    // negative deltaY = pinch out (should zoom in)
    let newMag = mag;
    if (deltaY > 0) {
      // Pinch in - zoom out
      newMag = Math.max(mag - step, minRatio);
    } else if (deltaY < 0) {
      // Pinch out - zoom in
      newMag = Math.min(mag + step, maxRatio);
    }

    // Only update if magnification changed
    if (newMag !== mag) {
      this.ngxImageZoom.zoomService.magnification = newMag;

      // If not already zooming, activate zoom
      if (!this.ngxImageZoom.zoomService.zoomingEnabled) {
        this.ngxImageZoom.zoomService.zoomOn(syntheticEvent || event);
      }

      // Update calculations
      this.ngxImageZoom.zoomService.calculateRatio();
      this.ngxImageZoom.zoomService.calculateZoomPosition(syntheticEvent || event);

      // Update zoom indicator
      this.setZoomScroll(newMag);
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Create a synthetic wheel event with coordinates converted to be relative to container
   */
  private _createSyntheticWheelEvent(originalEvent: WheelEvent, containerRect: DOMRect): WheelEvent {
    // Create a new synthetic event with relevant properties
    const syntheticEvent = new WheelEvent("wheel", {
      deltaY: originalEvent.deltaY,
      deltaX: originalEvent.deltaX,
      deltaZ: originalEvent.deltaZ,
      deltaMode: originalEvent.deltaMode,
      ctrlKey: originalEvent.ctrlKey,
      clientX: originalEvent.clientX,
      clientY: originalEvent.clientY,
      screenX: originalEvent.screenX,
      screenY: originalEvent.screenY
    });

    // Add offsetX/Y properties to make it compatible with ngx-image-zoom
    (syntheticEvent as any).offsetX = originalEvent.clientX - containerRect.left;
    (syntheticEvent as any).offsetY = originalEvent.clientY - containerRect.top;

    return syntheticEvent;
  }

  private _initImageZoom(skipZoomReset: boolean = false) {
    if (this.ngxImageZoom) {
      const renderedThumbnailHeight = this.ngxImageZoomEl.nativeElement.querySelector(".ngxImageZoomThumbnail").height;
      const thumbnailNaturalHeight =
        this.ngxImageZoomEl.nativeElement.querySelector(".ngxImageZoomThumbnail").naturalHeight;
      const renderRatio = renderedThumbnailHeight / thumbnailNaturalHeight;
      const renderedThumbnailWidth =
        this.ngxImageZoomEl.nativeElement.querySelector(".ngxImageZoomThumbnail").naturalWidth * renderRatio;

      this.ngxImageZoom.zoomService.thumbWidth = renderedThumbnailWidth;
      this.ngxImageZoom.zoomService.thumbHeight = renderedThumbnailHeight;
      this.ngxImageZoom.zoomService.minZoomRatio = renderedThumbnailWidth / this.naturalWidth;

      // Only reset the magnification and zoom scroll if not explicitly skipping this step
      // This helps when restoring from measuring mode where we want to maintain the previous zoom level
      if (!skipZoomReset) {
        // Get the minimum zoom ratio (fit to window)
        const minRatio = this.ngxImageZoom.zoomService.minZoomRatio;

        // Set magnification to the minimum ratio (fit to window) instead of 1
        this.ngxImageZoom.zoomService.magnification = minRatio;
        this.setZoomScroll(minRatio);
      }

      // Handle touchpad pinch gestures in Firefox
      // Convert them to zoom operations similar to Chrome
      if (isPlatformBrowser(this.platformId)) {
        // Add a direct wheel event listener that will handle pinch gestures
        // This approach should work better for Firefox touchpad gestures
        const container = this.ngxImageZoomEl.nativeElement;

        // Using a more focused wheel handler specifically for Firefox
        container.addEventListener(
          "wheel",
          (event: WheelEvent) => {
            if (event.ctrlKey) {
              this._handleFirefoxPinchZoom(event);
            }
          },
          { passive: false }
        );

        // Handle Firefox touchpad pinch gesture globally
        // This is needed because the Firefox pinch gesture doesn't always bubble up
        // to the container element properly
        document.addEventListener(
          "wheel",
          (event: WheelEvent) => {
            if (
              event.ctrlKey &&
              !this.touchMode &&
              this.show &&
              !this.isVeryLargeImage &&
              !this.zoomFrozen &&
              !this.isAnnotationMode
            ) {
              // Always prevent browser zoom
              event.preventDefault();
              event.stopPropagation();

              // Only handle if zoom component exists
              if (this.ngxImageZoom && this.ngxImageZoom.zoomService) {
                // Convert global coordinates to container-relative coordinates
                // First, get the container's position
                const zoomContainer = this.ngxImageZoomEl.nativeElement.querySelector(".ngxImageZoomContainer");
                if (!zoomContainer) {
                  return;
                }

                const rect = zoomContainer.getBoundingClientRect();

                // Check if event is within container bounds
                if (
                  event.clientX >= rect.left &&
                  event.clientX <= rect.right &&
                  event.clientY >= rect.top &&
                  event.clientY <= rect.bottom
                ) {
                  // Create a synthetic event with coordinates relative to the container
                  const syntheticEvent = this._createSyntheticWheelEvent(event, rect);

                  // Handle the pinch zoom using the synthetic event
                  this._handleFirefoxPinchZoom(event, syntheticEvent);
                }
              }
            }
          },
          { passive: false }
        );
      }

      this.ngxImageZoomEl.nativeElement.querySelector(".ngxImageZoomThumbnail").addEventListener(
        "wheel",
        (event: WheelEvent) => {
          // Always prevent browser zoom with ctrl key
          if (event.ctrlKey) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          if (this.ngxImageZoom.zoomService.zoomingEnabled) {
            return;
          }

          event.preventDefault();

          this.ngxImageZoom.zoomService.magnification = this.ngxImageZoom.zoomService.minZoomRatio;
          this.ngxImageZoom.zoomService.zoomOn(event);
          // Clear specific "Activate zoom first" notification if it exists
          if (this._zoomActivationNotification) {
            // Extract toast ID - ActiveToast has a toastId property of type number
            if (typeof this._zoomActivationNotification !== "string" && this._zoomActivationNotification.toastId) {
              this.popNotificationsService.clear(this._zoomActivationNotification.toastId);
            }
            this._zoomActivationNotification = null;
          }
          this.changeDetectorRef.markForCheck();
        },
        { once: true }
      );

      // Prevents the jarring resetting of the zoom when the mouse wanders off the image.
      (this.ngxImageZoom as any).zoomInstance.onMouseLeave = () => {};

      // Update the image element reference for coordinates tracking
      this._updateImageElementReference();

      (this.ngxImageZoom as any).zoomInstance.onClick = (event: MouseEvent) => {
        if (this.enableLens) {
          if (this.zoomingEnabled) {
            this.ngxImageZoom.zoomService.zoomOff();
          } else {
            this.ngxImageZoom.zoomService.zoomOn(event);
            this.snapTo1x();
            // Clear specific "Activate zoom first" notification if it exists
            if (this._zoomActivationNotification) {
              // Extract toast ID - ActiveToast has a toastId property of type number
              if (typeof this._zoomActivationNotification !== "string" && this._zoomActivationNotification.toastId) {
                this.popNotificationsService.clear(this._zoomActivationNotification.toastId);
              }
              this._zoomActivationNotification = null;
            }
          }
        } else {
          if (this.zoomingEnabled) {
            this.hide(null);
          } else {
            this.ngxImageZoom.zoomService.zoomOn(event);
            this.snapTo1x();
            // Clear specific "Activate zoom first" notification if it exists
            if (this._zoomActivationNotification) {
              // Extract toast ID - ActiveToast has a toastId property of type number
              if (typeof this._zoomActivationNotification !== "string" && this._zoomActivationNotification.toastId) {
                this.popNotificationsService.clear(this._zoomActivationNotification.toastId);
              }
              this._zoomActivationNotification = null;
            }
          }
        }
      };

      this.changeDetectorRef.markForCheck();
    } else {
      this.utilsService.delay(50).subscribe(() => {
        this._initImageZoom();
      });
    }
  }

  private _resetTouchZoom(): void {
    this.touchZoom.reset();
  }

  private _resetThumbnailSubscriptions() {
    if (this._hdThumbnailSubscription) {
      this._hdThumbnailSubscription.unsubscribe();
      this._hdThumbnailSubscription = null;
    }

    if (this._realThumbnailSubscription) {
      this._realThumbnailSubscription.unsubscribe();
      this._realThumbnailSubscription = null;
    }

    if (this._eagerLoadingSubscription) {
      this._eagerLoadingSubscription.unsubscribe();
      this._eagerLoadingSubscription = null;
    }

    this.hdThumbnail = null;
    this.realThumbnail = null;
  }

  private _initThumbnailSubscriptions(): Subscription {
    // Reset any existing subscriptions
    this._resetThumbnailSubscriptions();

    const subscriptions = new Subscription();

    if (this._imageSubscription) {
      this._imageSubscription.unsubscribe();
    }

    this._imageSubscription = this.store$
      .pipe(
        select(selectImage, this.id),
        filter(image => !!image),
        take(1),
        tap(image => {
          this.image = image;

          // Check if current user is the image owner
          this.currentUser$.pipe(take(1)).subscribe(user => {
            if (user && this.image && user.id === this.image.user) {
              this.isImageOwner = true;
              this.changeDetectorRef.markForCheck();
            }
          });

          this.revision = this.imageService.getRevision(image, this.revisionLabel);
          this.naturalWidth = this.revision.w;
          this.naturalHeight = this.revision.h;
          this.maxZoom = image.maxZoom || image.defaultMaxZoom || 8;

          // Load solution matrix for coordinate calculation
          if (this.revision?.solution?.id) {
            // If matrix was passed from parent component, use it directly
            if (this.externalSolutionMatrix) {
              this.advancedSolutionMatrix = this.externalSolutionMatrix;
              this.loadingAdvancedSolutionMatrix = false;
              this.isMatrixLoaded$.next(!!this.externalSolutionMatrix);
              this.changeDetectorRef.markForCheck();
            } else {
              // Use the solvedCoordinatesService to load the matrix
              this.loadingAdvancedSolutionMatrix = true;
              this.solvedCoordinatesService.ensureMatrixLoaded(this.revision.solution.id).subscribe(matrix => {
                this.advancedSolutionMatrix = matrix;
                this.loadingAdvancedSolutionMatrix = false;
                this.isMatrixLoaded$.next(!!matrix);

                // Check if we have a pending activation request or there's a measurements parameter in the URL
                if (!!matrix) {
                  if (this.pendingMeasurementToolActivation && !this.isMeasuringMode) {
                    // Try to activate immediately if we can
                    if (this.isStaticImageLoaded$.value && this.isFullscreenActive$.value) {
                      this._activateMeasuringMode();
                      this.pendingMeasurementToolActivation = false;
                    }
                  } else if (this.isBrowser) {
                    // Check if there's a measurements parameter in the URL
                    const currentUrl = new URL(this.windowRef.nativeWindow.location.href);
                    const measurementsParam = currentUrl.searchParams.get("measurements");

                    if (measurementsParam && !this.isMeasuringMode) {
                      this.isMeasuringRequested$.next(true);

                      // Check if we can activate immediately
                      if (this.isStaticImageLoaded$.value && this.isFullscreenActive$.value) {
                        this._activateMeasuringMode();
                      } else {
                        // Otherwise set pending flag
                        this.pendingMeasurementToolActivation = true;
                      }
                    }
                  }
                }

                this.changeDetectorRef.markForCheck();
              });
            }
          } else {
            // No solution ID available
            this.isMatrixLoaded$.next(false);
          }

          this.hdThumbnailLoading = true;
          this._hdLoadingProgressSubject.next(0);

          this.changeDetectorRef.markForCheck();

          this.currentUser$.pipe(take(1)).subscribe(user => {
            const limit = image.fullSizeDisplayLimitation;
            this.allowReal =
              !this.respectFullSizeDisplayLimitation ||
              limit === FullSizeLimitationDisplayOptions.EVERYBODY ||
              (limit === FullSizeLimitationDisplayOptions.MEMBERS && !!user) ||
              (limit === FullSizeLimitationDisplayOptions.PAYING && !!user && !!user.validSubscription) ||
              (limit === FullSizeLimitationDisplayOptions.ME && !!user && user.id === image.user);
            this.changeDetectorRef.markForCheck();
          });
        })
      )

      .subscribe(() => {
        // Check if the image is a GIF
        this.isGif = this.revision.imageFile && this.revision.imageFile.toLowerCase().endsWith(".gif");

        // For GIFs, always use non-touch mode
        if (this.isGif && this.touchMode) {
          this.setTouchMouseMode(false);
        }

        if (this.isGif) {
          // For GIFs, use the original file directly for both HD and REAL thumbnails
          this.hdThumbnailLoading = true;
          this.realThumbnailLoading = true;

          this._hdLoadingProgressSubject.next(0);
          this._realLoadingProgressSubject.next(0);

          this.imageService
            .loadImageFile(this.revision.imageFile, (progress: number) => {
              this._hdLoadingProgressSubject.next(progress);
              this._realLoadingProgressSubject.next(progress);
            })
            .pipe(
              switchMap(url => this._preloadImage(url).pipe(map(() => this.domSanitizer.bypassSecurityTrustUrl(url))))
            )
            .subscribe(url => {
              this.hdThumbnail = url;
              this.realThumbnail = url;
              this.realThumbnailUnsafeUrl = this.revision.imageFile;

              this.hdThumbnailLoading = false;
              this.realThumbnailLoading = false;

              if (this.touchMode && !this.isGif) {
                this.initTouchZoom();
              }

              this.changeDetectorRef.markForCheck();
            });
        } else {
          // Normal image handling for non-GIFs
          this._hdThumbnailSubscription = this.store$
            .select(selectThumbnail, this._getHdOptions())
            .pipe(
              tap(() => {
                this.changeDetectorRef.markForCheck();
              }),
              filter(thumbnail => !!thumbnail),
              switchMap(thumbnail =>
                this.imageService
                  .loadImageFile(thumbnail.url, (progress: number) => {
                    this._hdLoadingProgressSubject.next(progress);
                  })
                  .pipe(
                    switchMap(url =>
                      this._preloadImage(url).pipe(
                        map(() => this.domSanitizer.bypassSecurityTrustUrl(url)),
                        tap(() =>
                          this.store$.dispatch(
                            new LoadThumbnail({
                              data: this._getRealOptions(),
                              bustCache: false
                            })
                          )
                        ),
                        tap(() => {
                          this.hdThumbnailLoading = false;
                          this.changeDetectorRef.markForCheck();
                        })
                      )
                    )
                  )
              )
            )
            .subscribe(url => {
              this.hdThumbnail = url;
              this.changeDetectorRef.markForCheck();
            });

          this._realThumbnailSubscription = this.store$
            .select(selectThumbnail, this._getRealOptions())
            .pipe(
              tap(() => {
                this.realThumbnailLoading = true;
                this._realLoadingProgressSubject.next(0);
                this.changeDetectorRef.markForCheck();
              }),
              filter(thumbnail => !!thumbnail),
              tap(thumbnail => {
                this.realThumbnailUnsafeUrl = thumbnail.url;
                this.changeDetectorRef.markForCheck();
              }),
              switchMap(thumbnail =>
                this.imageService.loadImageFile(thumbnail.url, (progress: number) => {
                  // Cap at 99% to avoid showing 100% before canvas is ready
                  this._realLoadingProgressSubject.next(Math.min(progress, 99));
                })
              )
            )
            .subscribe(url => {
              this.realThumbnail = url;
              this.realThumbnailLoading = false;

              if (this.touchMode && !this.isGif) {
                this.initTouchZoom();
              }

              this.changeDetectorRef.markForCheck();
            });

          subscriptions.add(this._hdThumbnailSubscription);
          subscriptions.add(this._realThumbnailSubscription);

          this.store$.dispatch(new LoadThumbnail({ data: this._getHdOptions(), bustCache: false }));
        }
      });

    subscriptions.add(this._imageSubscription);

    return subscriptions;
  }

  private _resetCanvas(): void {
    // Clean up all canvas resources through TouchZoomService
    this.touchZoom.destroy();

    // Reset any local canvas state
    this.canvasLoading = false;
  }

  private _setZoomLensSize(): void {
    this.zoomLensSize = Math.floor(this.windowRef.nativeWindow.innerWidth / 4);
    if (this.ngxImageZoom) {
      this.ngxImageZoom.zoomService.lensWidth = this.zoomLensSize;
      this.ngxImageZoom.zoomService.lensHeight = this.zoomLensSize;
    }
  }

  private _setZoomIndicatorTimeout(): void {
    if (isPlatformBrowser(this.platformId)) {
      // No need to clear timeout when using utilsService.delay

      this.utilsService.delay(this._zoomIndicatorTimeoutDuration).subscribe(() => {
        this.showZoomIndicator = false;
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  private _getHdOptions(): Omit<ImageThumbnailInterface, "url"> {
    return {
      id: this.id,
      revision: this.revisionLabel,
      alias: this.anonymized ? ImageAlias.HD_ANONYMIZED : ImageAlias.QHD
    };
  }

  private _getRealOptions(): Omit<ImageThumbnailInterface, "url"> {
    return {
      id: this.id,
      revision: this.revisionLabel,
      alias: this.anonymized ? ImageAlias.REAL_ANONYMIZED : ImageAlias.REAL
    };
  }

  private _preloadImage(url: string): Observable<void> {
    return new Observable(subscriber => {
      const img = new Image();
      img.decoding = "async";
      img.onload = () => {
        subscriber.next();
        subscriber.complete();
      };
      img.onerror = err => subscriber.error(err);
      img.src = url;
    });
  }

  public dismissInstruction(cookieKey: string): void {
    if (isPlatformBrowser(this.platformId)) {
      this.cookieService.put(`fullscreen-instruction-dismissed-${cookieKey}`, "true", {
        expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000) // 1 year
      });
      this.changeDetectorRef.markForCheck();
    }
  }

  public isInstructionDismissed(cookieKey: string): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return this.cookieService.get(`fullscreen-instruction-dismissed-${cookieKey}`) === "true";
  }

  /**
   * Safely check if an event is a touch event without directly using instanceof TouchEvent
   * This handles browsers where TouchEvent might not be defined
   */
  private _isTouchEvent(event: any): boolean {
    if (!event) {
      return false;
    }

    // Check for touch event properties instead of using instanceof
    return (
      typeof event.touches !== "undefined" ||
      typeof event.changedTouches !== "undefined" ||
      (event.type && event.type.startsWith("touch"))
    );
  }
}
